import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTimezoneSelect, allTimezones, ITimezoneOption } from 'react-timezone-select';

import { useCreateInterviewAgent } from '@/store/createInterviewAgent';
import { ScreeningType } from '@/types/enums';
import { Client } from '@/types/Client';
import { Job } from '@/types/Job';
import { CandidateWithJobContext, Candidate } from '@/types/Candidate';

// Helper functions
const getDefaultDeadline = () => {
  const now = new Date();
  let businessDaysAdded = 0;
  const currentDate = new Date(now);

  while (businessDaysAdded < 3) {
    currentDate.setDate(currentDate.getDate() + 1);
    const dayOfWeek = currentDate.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDaysAdded++;
    }
  }

  return currentDate;
};

const getDefaultDeadlineTime = () => {
  const time = new Date();
  time.setHours(23, 59, 0, 0);
  return time;
};

// Form types
export interface FormValues {
  deadline: Date | null;
  deadline_time: Date | null;
  timezone: { value: ITimezoneOption };
  sessionName: string;
  client?: { value: Client };
  job?: { value: Job };
  candidate?: { value: CandidateWithJobContext };
  directCandidate?: Candidate;
}

export const useSessionForm = () => {
  const { screeningType, selectedTab, deadline, deadlineTime, timezone, sessionName, client, job, candidate } =
    useCreateInterviewAgent();

  // Timezone setup
  const { options, parseTimezone } = useTimezoneSelect({
    timezones: allTimezones,
    labelStyle: 'original'
  });

  // Use selectedTab from store instead of deriving from screeningType

  // Validation rules
  const validationRules = useMemo(
    () => ({
      client: {
        required: selectedTab === 'hierarchical' ? 'Client is required' : false
      },
      job: {
        required: selectedTab === 'hierarchical' ? 'Job is required' : false
      },
      candidate: {
        required: selectedTab === 'hierarchical' ? 'Candidate is required' : false
      },
      deadline: {
        required: 'Deadline is required',
        validate: (value: Date | null, formValues: FormValues) => {
          if (!value) return 'Deadline is required';

          const deadlineTime = formValues.deadline_time;
          if (!deadlineTime) return true; // Let deadline_time validation handle this

          // Combine date and time
          const combined = new Date(value);
          combined.setHours(deadlineTime.getHours(), deadlineTime.getMinutes(), 0, 0);

          const now = new Date();
          if (combined <= now) {
            return 'Deadline must be in the future';
          }

          return true;
        }
      },
      deadline_time: {
        required: 'Time is required',
        validate: (value: Date | null, formValues: FormValues) => {
          if (!value) return 'Time is required';

          const deadlineDate = formValues.deadline;
          if (!deadlineDate) return true; // Let deadline validation handle this

          // Combine date and time
          const combined = new Date(deadlineDate);
          combined.setHours(value.getHours(), value.getMinutes(), 0, 0);

          const now = new Date();
          if (combined <= now) {
            return 'Deadline must be in the future';
          }

          return true;
        }
      }
    }),
    [selectedTab]
  );

  // Default form values
  const defaultValues = useMemo(
    () => ({
      deadline: deadline || getDefaultDeadline(),
      deadline_time: deadlineTime || getDefaultDeadlineTime(),
      timezone: timezone ?? {
        value: parseTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone)
      },
      sessionName: sessionName,
      client:
        screeningType === ScreeningType.JobInterview && client ? { value: client } : undefined,
      job: screeningType === ScreeningType.JobInterview && job ? { value: job } : undefined,
      candidate:
        screeningType === ScreeningType.JobInterview && candidate
          ? { value: candidate as CandidateWithJobContext }
          : undefined,
      directCandidate:
        screeningType === ScreeningType.ResumeScreening && candidate
          ? (candidate as Candidate)
          : undefined
    }),
    [
      deadline,
      deadlineTime,
      timezone,
      sessionName,
      screeningType,
      client,
      job,
      candidate,
      parseTimezone
    ]
  );

  // Form setup
  const form = useForm<FormValues>({
    mode: 'onChange',
    defaultValues
  });

  return {
    form,
    selectedTab,
    validationRules,
    options,
    parseTimezone
  };
};
